import React, { useRef, Children } from 'react';
import { AlertCircleIcon, TrendingDownIcon, ClockIcon, UsersIcon, DollarSignIcon } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
export function ProblemSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  // Parallax for the "Hidden Cost" card
  const cardY = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const cardRotate = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0, -1]);
  const painPoints = [{
    icon: TrendingDownIcon,
    title: 'The Ghost Town',
    description: 'You hire a rep, and they disappear 3 days later.'
  }, {
    icon: AlertCircleIcon,
    title: 'Feast or Famine',
    description: 'One month you have 8 good hires, the next month you have zero.'
  }, {
    icon: ClockIcon,
    title: 'Wasted Leadership Time',
    description: 'You spend hours interviewing people who should never have been in the room.'
  }, {
    icon: UsersIcon,
    title: 'Failure to Launch',
    description: 'Rookies quit before they knock their first 100 doors.'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section ref={sectionRef} className="section-padding bg-white overflow-hidden" data-id="element-865">
      <div className="container-custom" data-id="element-866">
        <motion.div className="max-w-3xl mx-auto text-center mb-12 md:mb-16" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }} data-id="element-867">
          <h2 className="mb-4 md:mb-6" data-id="element-868">Why "Post and Pray" Is Failing You</h2>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-869">
            You aren't bad at recruiting. You just don't have a system. You are
            trying to scale a sales team{' '}
            <span className="font-bold text-deep-blue" data-id="element-870">
              on luck, and it's costing you money
            </span>
            .
          </p>
        </motion.div>

        <motion.div className="max-w-4xl mx-auto mb-12 md:mb-16" style={{
        y: cardY,
        rotateX: cardRotate
      }} initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }} data-id="element-871">
          <div className="bg-gradient-to-br from-mint/10 to-mint/5 rounded-2xl p-6 md:p-8 lg:p-12 border-2 border-mint/30 shadow-lg" data-id="element-872">
            <div className="flex items-center justify-center mb-4 md:mb-6" data-id="element-873">
              <motion.div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-mint rounded-xl flex items-center justify-center" animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 1
            }} data-id="element-874">
                <DollarSignIcon className="h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7 text-deep-blue" data-id="element-875" />
              </motion.div>
            </div>
            <h3 className="text-center mb-6 md:mb-8 text-deep-blue text-lg md:text-xl lg:text-2xl" data-id="element-876">
              The Hidden Cost
            </h3>
            <div className="grid grid-cols-2 gap-3 md:gap-4 lg:gap-6" data-id="element-877">
              {[{
              value: 'Lost Revenue',
              label: 'Per empty seat (Opportunity Cost)'
            }, {
              value: '15+ Hours/Week',
              label: 'Of leadership time burned on bad interviews'
            }, {
              value: '6–12 Months',
              label: "Of growth lost because you can't find people"
            }, {
              value: 'Constant Stress',
              label: 'Always wondering where the next hire is coming from'
            }].map((item, index) => <motion.div key={index} className="bg-white rounded-xl p-4 md:p-5 lg:p-6 border border-mint/20 shadow-sm" initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} whileHover={{
              scale: 1.02,
              borderColor: 'rgba(127, 224, 195, 0.5)',
              transition: {
                duration: 0.2
              }
            }} data-id="element-878">
                  <div className="text-xl md:text-2xl font-bold text-mint mb-1 md:mb-2" data-id="element-879">
                    {item.value}
                  </div>
                  <p className="text-gray-700 text-xs md:text-sm" data-id="element-880">
                    {item.label}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8 max-w-5xl mx-auto" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-881">
          {painPoints.map((pain, index) => {
          const Icon = pain.icon;
          return <motion.div key={index} className="bg-gray-50 rounded-xl p-5 md:p-6 lg:p-8 border border-gray-200 hover:border-mint/50 transition-all" variants={itemVariants} whileHover={{
            y: -3,
            boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)',
            transition: {
              duration: 0.2
            }
          }} data-id="element-882">
                <div className="flex items-start gap-3 md:gap-4 mb-3" data-id="element-883">
                  <motion.div className="w-10 h-10 md:w-12 md:h-12 bg-mint/10 rounded-lg flex items-center justify-center flex-shrink-0" whileHover={{
                scale: 1.1,
                rotate: -5
              }} data-id="element-884">
                    <Icon className="h-5 w-5 md:h-6 md:w-6 text-mint" data-id="element-885" />
                  </motion.div>
                  <h4 className="text-base md:text-lg flex-1 leading-tight pt-1 md:pt-2 text-deep-blue" data-id="element-886">
                    {pain.title}
                  </h4>
                </div>
                <p className="text-gray-600 text-sm md:text-base" data-id="element-887">
                  {pain.description}
                </p>
              </motion.div>;
        })}
        </motion.div>

        <motion.div className="mt-12 md:mt-16 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} data-id="element-888">
          <div className="inline-block bg-deep-blue/5 border-l-4 border-mint px-6 md:px-8 py-4 md:py-6 rounded-r-xl max-w-3xl" data-id="element-889">
            <p className="text-base md:text-lg text-gray-700 italic" data-id="element-890">
              "You don't need to work harder at recruiting. You need a system
              that does the heavy lifting for you."
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}